/**
 * "Ask how accurate the background OCR was": from the notifications socket to
 * the open note page.
 *
 * The synchronous Image to Text path opens the `ocr_accuracy` feedback modal
 * itself, because it inserts the text. The background path's text arrives via
 * the `note:ocr-ready` notification instead, handled by a hook mounted at the
 * app root that cannot reach the note page's modal state. The socket hook
 * dispatches this only when the user is on that note's page; the page
 * (useNoteAttachments) opens the modal through the same entry point the
 * synchronous path uses.
 *
 * The background transcription (`task:complete` for `note-transcription`)
 * reuses this channel with `category: 'stt_accuracy'`; absent means OCR.
 */

const EVENT_NAME = 'bower:note-ocr-feedback';

export interface NoteOcrFeedbackDetail {
  readonly noteId: string;
  readonly traceId: string;
  /** Which accuracy prompt to open; defaults to `ocr_accuracy`. */
  readonly category?: 'ocr_accuracy' | 'stt_accuracy';
}

export function requestNoteOcrFeedback(detail: NoteOcrFeedbackDetail): void {
  window.dispatchEvent(new CustomEvent<NoteOcrFeedbackDetail>(EVENT_NAME, { detail }));
}

/** Returns a cleanup function suitable for a `useEffect` return. */
export function subscribeNoteOcrFeedback(
  handler: (detail: NoteOcrFeedbackDetail) => void,
): () => void {
  const listener = (event: Event) => handler((event as CustomEvent<NoteOcrFeedbackDetail>).detail);
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}
